import React from 'react';
import { redirect, Navigate } from 'react-router-dom';
import { Toggler } from '~/platform/utilities/feature-toggles';

import App from './containers/App';
import AuthCallbackHandler from './containers/AuthCallbackHandler';
import AuthenticatedLayoutContainer from './containers/AuthenticatedLayoutContainer';
import ClaimantOverviewPage from './containers/ClaimantOverviewPage';
import ClaimantSearchPage from './containers/ClaimantSearchPage';
import ClaimantSubmissionHistoryPage from './containers/ClaimantSubmissionHistoryPage';
import ClaimantSubmitFormsPage from './containers/ClaimantSubmitFormsPage';
import DashboardPage from './containers/DashboardPage';
import ErrorBoundary from './components/Error/ErrorBoundary';
import HelpPage from './containers/HelpPage';
import InProgressFormsPage from './containers/InProgressFormsPage';
import LandingPage from './containers/LandingPage';
import LoginContainer from './containers/LoginContainer';
import SubmitFormsAndFilesPage from './containers/SubmitFormsAndFilesPage';
import SubmissionHistoryPage from './containers/SubmissionHistoryPage';
import POARequestDetailsPage from './containers/POARequestDetailsPage';
import POARequestSearchPage from './containers/POARequestSearchPage';
import PublicLayoutContainer from './containers/PublicLayoutContainer';
import QuickSubmitConfirmationPage from './containers/QuickSubmitConfirmationPage';
import QuickSubmitHelpPage from './containers/QuickSubmitHelpPage';
import QuickSubmitLayout from './containers/QuickSubmitLayout';
import SignedInLayout from './containers/SignedInLayout';
import SubmissionsPage from './containers/SubmissionsPage';

import { userPromise } from './utilities/auth';
import { shouldRevalidateUser } from './utilities/helpers';
import { shouldRevalidateHistory } from './utilities/combinedHistory';
import store from './utilities/store';
import {
  QUICK_SUBMIT_EFORM_EDIT_PATH,
  QUICK_SUBMIT_EFORM_NEW_PATH,
  QUICK_SUBMIT_PAGES,
  QUICK_SUBMIT_ROUTE_ID,
  quickSubmitHandle,
} from './utilities/quickSubmit';
import { getSignInUrl } from './utilities/constants';

const forEachRoute = (callbackFn, route) => {
  callbackFn(route);
  route.children?.forEach(childRoute => forEachRoute(callbackFn, childRoute));
  return route;
};

/* eslint-disable no-param-reassign */
const addSignInRedirection = route => {
  const { loader } = route;
  if (!loader) return;

  route.loader = async ({ params, request }) => {
    if (await userPromise) {
      try {
        return await loader({ params, request });
      } catch (e) {
        // Only rethrow non-401 errors
        if (!(e instanceof Response) || e.status !== 401) {
          throw e;
        }
      }
    }

    // Handle both initial auth and token expiration (401s)
    throw redirect(
      getSignInUrl({
        returnUrl: request.url,
      }),
    );
  };
};
/* eslint-enable no-param-reassign */

const routes = [
  {
    id: 'root',
    path: '/',
    element: <App />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <PublicLayoutContainer />,
        children: [
          {
            path: 'sign-in',
            element: (
              <LoginContainer title="Sign in or create an account | Accredited Representative Portal | Veterans Affairs" />
            ),
          },
          {
            path: 'auth/login/callback',
            element: <AuthCallbackHandler />,
            loader: AuthCallbackHandler.loader,
          },
        ],
      },
      {
        element: <AuthenticatedLayoutContainer />,
        loader() {
          return userPromise;
        },
        shouldRevalidate: shouldRevalidateUser,
        children: [
          {
            index: true,
            element: (
              <LandingPage title="Welcome to the Accredited Representative Portal | Veterans Affairs" />
            ),
          },
          forEachRoute(addSignInRedirection, {
            element: <SignedInLayout />,
            children: [
              {
                path: 'dashboard',
                element: (
                  <DashboardPage title="Dashboard | Accredited Representative Portal | Veterans Affairs" />
                ),
                loader: DashboardPage.loader,
              },
              {
                path: 'representation-requests',
                element: (
                  <POARequestSearchPage title="Representation requests | Accredited Representative Portal | Veterans Affairs" />
                ),
                loader: POARequestSearchPage.loader,
              },
              {
                path: 'submissions',
                element: (
                  <Toggler
                    toggleName={
                      Toggler.TOGGLE_NAMES
                        .accreditedRepresentativePortalCdUpdateMain
                    }
                  >
                    <Toggler.Enabled>
                      <Navigate to="/submit-forms-and-files" replace />
                    </Toggler.Enabled>
                    <Toggler.Disabled>
                      <SubmissionsPage title="Submissions | Accredited Representative Portal | Veterans Affairs" />
                    </Toggler.Disabled>
                  </Toggler>
                ),
                loader: SubmissionsPage.loader,
              },
              {
                path: 'submit-forms-and-files',
                element: (
                  <SubmitFormsAndFilesPage title="Submit forms and files | Accredited Representative Portal | Veterans Affairs" />
                ),
              },
              {
                id: QUICK_SUBMIT_ROUTE_ID,
                path: 'submit-forms-and-files/upload',
                loader: QuickSubmitLayout.loader,
                shouldRevalidate: QuickSubmitLayout.shouldRevalidate,
                // The layout sits on the parent, so a page move keeps the
                // embed alive. Each child names its page in its handle. The
                // layout draws the embed, so the embed's pages need no
                // element of their own.
                element: <QuickSubmitLayout />,
                children: [
                  {
                    index: true,
                    handle: quickSubmitHandle(QUICK_SUBMIT_PAGES.UPLOAD),
                  },
                  {
                    path: 'confirmation',
                    element: <QuickSubmitConfirmationPage />,
                    handle: quickSubmitHandle(QUICK_SUBMIT_PAGES.CONFIRMATION),
                  },
                  {
                    path: 'eforms',
                    handle: quickSubmitHandle(QUICK_SUBMIT_PAGES.EFORMS),
                  },
                  {
                    path: QUICK_SUBMIT_EFORM_NEW_PATH,
                    handle: quickSubmitHandle(QUICK_SUBMIT_PAGES.EFORMS),
                  },
                  {
                    path: QUICK_SUBMIT_EFORM_EDIT_PATH,
                    handle: quickSubmitHandle(QUICK_SUBMIT_PAGES.EFORMS),
                  },
                ],
              },
              {
                path: 'submission-history',
                element: (
                  <SubmissionHistoryPage title="Submission history | Accredited Representative Portal | Veterans Affairs" />
                ),
                loader: SubmissionHistoryPage.loader,
                shouldRevalidate: shouldRevalidateHistory(store),
              },
              {
                path: 'find-claimant',
                element: (
                  <ClaimantSearchPage title="Find claimant | Accredited Representative Portal | Veterans Affairs" />
                ),
                loader: ClaimantSearchPage.loader,
              },
              {
                path: 'find-claimant/claimant-overview/:claimantId',
                element: <ClaimantOverviewPage />,
                loader: ClaimantOverviewPage.loader,
              },
              {
                path: 'find-claimant/submission-history/:claimantId',
                element: <ClaimantSubmissionHistoryPage />,
                loader: ClaimantSubmissionHistoryPage.loader,
                shouldRevalidate: shouldRevalidateHistory(store),
              },
              {
                path: 'find-claimant/submit-forms-and-files/:claimantId',
                element: <ClaimantSubmitFormsPage />,
                loader: ClaimantSubmitFormsPage.loader,
              },
              {
                path: 'representation-requests/:id',
                element: <POARequestDetailsPage />,
                loader: POARequestDetailsPage.loader,
                children: [
                  {
                    path: 'decision',
                    action: POARequestDetailsPage.createDecisionAction,
                  },
                ],
              },
              {
                path: 'in-progress-forms',
                element: (
                  <InProgressFormsPage title="In-progress forms | Accredited Representative Portal | Veterans Affairs" />
                ),
                loader: InProgressFormsPage.loader,
              },
              {
                path: 'help',
                element: (
                  <HelpPage title="Get help with the Accredited Representative Portal | Accredited Representative Portal | Veterans Affairs" />
                ),
              },
              {
                path: 'help/quicksubmit',
                element: <QuickSubmitHelpPage />,
              },
            ],
          }),
        ],
      },
    ],
  },
];

export default routes;
